import { useCallback, useEffect, useMemo, useState } from 'react'

import { fetchSearch, fetchSimpleReport } from 'api/report/realTime'
import {
  ConversationFeedItemWithDetails,
  ConversationFeedMetrics,
  ConversationFeedSearchMetrics,
  RoutingFallbackTypes,
} from 'api/report/realTime/types'
import useReportSWR from 'hooks/useReportSWR'

const DEFAULT_EMPTY_RESPONSE = {
  conversations: [],
  error: null,
  loading: false,
  mutate: () => null,
}

const SEARCH_METRICS = {
  [ConversationFeedMetrics.MISSED]: ConversationFeedSearchMetrics.MISSED,
  [ConversationFeedMetrics.CSAT]: ConversationFeedSearchMetrics.CSAT,
}

const SEARCH_DETAILS = {
  [ConversationFeedMetrics.MISSED]: (found, conversation) => {
    const timeToAdd =
      conversation.reason === RoutingFallbackTypes.OFFLINE ? 60000 : 0
    return {
      agentId: `${found.firstRoutingAction.participants[0]}`,
      endUserId: `${found.endUserId}`,
      timestamp:
        (found.routingFallbackSummary?.fallbackActionAt ??
          found.firstRoutingAction.routedAt) + timeToAdd,
      messageId:
        found.routingFallbackSummary?.messageId ??
        found.firstRoutingAction.messageId,
    }
  },
  [ConversationFeedMetrics.CSAT]: (found) => ({
    agentId: `${found.latestLiveChatRating?.ratedLiveAgentId}`,
    endUserId: `${found.endUserId}`,
    timestamp: found.latestLiveChatRating.ratedAt,
    ratingText: found.latestLiveChatRating.ratingText,
    messageId: found.latestLiveChatRating.messageId,
  }),
}

export const useConversationData = (
  swrKey: ConversationFeedMetrics,
  botId,
  allowFetch = true
) => {
  const [conversations, setConversations] = useState<
    ConversationFeedItemWithDetails[]
  >([])

  const query = useMemo(
    () => ({
      metric: swrKey,
      // csat report fails to filter out bots so must do it manually for now.
      // can remove this from final query if report is fixed
      botId,
    }),
    [botId, swrKey]
  )

  const { data, error, mutate } = useReportSWR<
    ConversationFeedItemWithDetails[] | undefined
  >(
    swrKey,
    query,
    botId && allowFetch ? () => fetchSimpleReport(query) : () => []
  )

  const searchQuery = useMemo(
    () => ({
      metric: SEARCH_METRICS[swrKey],
      botId,
    }),
    [botId, swrKey]
  )

  const { data: searchData, mutate: reFetchSearch } = useReportSWR<
    any[] | undefined
  >(
    SEARCH_METRICS[swrKey],
    searchQuery,
    botId && allowFetch ? () => fetchSearch(searchQuery) : () => [],
    { revalidateOnFocus: false }
  )

  // add newly polled reporting data OR update conversations with their meta information required by the UI
  useEffect(() => {
    const currentIds = conversations.map((c) => c.id)
    const dataToAdd = (data ?? []).filter((c) => !currentIds.includes(c.id))
    if (dataToAdd.length) {
      setConversations([...conversations, ...dataToAdd])
      reFetchSearch()
    }
  }, [conversations, data, reFetchSearch])

  const getExtraConvoDetails = useCallback(
    (conversation) => {
      const { id } = conversation
      const found = searchData?.find((c) => c.id === id)
      return found ? SEARCH_DETAILS[swrKey](found, conversation) : {}
    },
    [searchData, swrKey]
  )

  useEffect(() => {
    const allConvosUpdated = conversations.every((c) => !!c.timestamp)
    !allConvosUpdated &&
      setConversations((prevConvos) =>
        prevConvos?.map((c) => {
          if (!c.timestamp) {
            return { ...c, ...getExtraConvoDetails(c) }
          }
          return c
        })
      )
  }, [searchData, getExtraConvoDetails, conversations])

  if (!allowFetch) {
    return DEFAULT_EMPTY_RESPONSE
  }

  return {
    conversations,
    error,
    loading: !data,
    mutate,
  }
}
